import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

export default function BeginnersArticle() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = `${t('beginnersH1')} | CSSBuy Spreadsheet`;
  }, [t]);

  const [tocOpen, setTocOpen] = useState(false);

  const tocItems = [
    { id: 'tldr', label: t('articleTldr') },
    { id: 'intro', label: 'Introdução' },
    { id: 'what-is', label: t('beginnersSection1H2') },
    { id: 'getting-started', label: t('beginnersSection2H2') },
    { id: 'research', label: t('beginnersSection3H2') },
    { id: 'tips', label: t('beginnersSection4H2') },
    { id: 'comparison', label: t('beginnersSection5H2') },
    { id: 'safety', label: t('articleSafety') },
    { id: 'faq', label: t('articleFaq') },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        <div className="max-w-6xl mx-auto px-4 md:px-6 pt-24 pb-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors">{t('breadcrumbHome')}</Link>
            <i className="ri-arrow-right-s-line" style={{ width: 16, height: 16 }} />
            <span className="text-foreground-700">{t('breadcrumbArticles')}</span>
          </nav>
        </div>

        <article className="py-8 md:py-12">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Article Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 text-sm text-foreground-500 mb-4">
                <span>{t('articlePublished')}: 27 Ago 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300" />
                <span>{t('articleUpdated')}: 27 Ago 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300" />
                <span>8 {t('articleReadTime')}</span>
              </div>
              <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 mb-6">
                {t('beginnersH1')}
              </h1>
              <p className="text-lg text-foreground-700 leading-relaxed">
                {t('beginnersIntro')}
              </p>
            </div>

            {/* Hero Image */}
            <div className="rounded-2xl overflow-hidden mb-10 shadow-lg">
              <img
                src="https://readdy.ai/api/search-image?query=Person%20learning%20to%20research%20fashion%20products%20on%20laptop%20with%20spreadsheet%20and%20notes%20warm%20minimal%20workspace%20editorial%20photography%20soft%20natural%20lighting%20clean%20aesthetic&width=1200&height=600&seq=article-beginners-hero&orientation=landscape"
                alt="CSSBuy Spreadsheet Beginners Guide"
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Table of Contents */}
            <div className="bg-background-100 rounded-xl p-5 mb-10">
              <button
                onClick={() => setTocOpen(!tocOpen)}
                className="w-full flex items-center justify-between"
              >
                <h2 className="font-semibold text-foreground-950">{t('articleContents')}</h2>
                <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${tocOpen ? 'rotate-180' : ''}`} style={{ width: 20, height: 20 }} />
              </button>
              {tocOpen && (
                <ul className="mt-3 space-y-2">
                  {tocItems.map((item) => (
                    <li key={item.id}>
                      <a href={`#${item.id}`} className="text-sm text-foreground-700 hover:text-primary-600 transition-colors">
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* TL;DR */}
            <div id="tldr" className="bg-accent-50 border border-accent-200 rounded-2xl p-6 md:p-8 mb-10">
              <h2 className="font-heading text-xl text-foreground-950 mb-3">{t('articleTldr')}</h2>
              <p className="text-sm text-foreground-700 leading-relaxed">
                O CSSBuy Spreadsheet é uma ferramenta organizada para pesquisa de produtos. Use as categorias para encontrar itens, compare opções, verifique QC antes de enviar, e consulte guias sobre shipping e custos. Sempre verifique informações atualizadas antes de comprar.
              </p>
            </div>

            {/* Introduction */}
            <div id="intro" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">Introdução</h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                Se você está começando a explorar o mundo das compras online através de agents, o <Link to="/cssbuy-spreadsheet" className="text-primary-600 hover:underline">CSSBuy Spreadsheet</Link> é o recurso ideal para organizar sua pesquisa. Este guia foi criado para ajudar iniciantes a entender como usar essa ferramenta de forma eficiente.
              </p>
              <p className="text-foreground-700 leading-relaxed">
                A pesquisa de produtos é uma etapa fundamental antes de qualquer compra. Com o CSSBuy Spreadsheet, você tem acesso a categorias organizadas, informações sobre QC, warehouse, shipping e comparações que facilitam a tomada de decisão.
              </p>
            </div>

            {/* What is */}
            <div id="what-is" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">{t('beginnersSection1H2')}</h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                {t('beginnersSection1Text')}
              </p>
              <p className="text-foreground-700 leading-relaxed">
                Diferente de simples listas de produtos, o CSSBuy Spreadsheet oferece uma estrutura organizada que permite comparar opções, verificar detalhes de qualidade e entender os custos envolvidos antes de fazer uma compra. É uma ferramenta de pesquisa que complementa a experiência de uso do site principal.
              </p>
            </div>

            {/* Getting Started */}
            <div id="getting-started" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">{t('beginnersSection2H2')}</h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                {t('beginnersSection2Text')}
              </p>
              <p className="text-foreground-700 leading-relaxed">
                Comece explorando as categorias que mais lhe interessam. Cada categoria contém informações sobre produtos relevantes, com detalhes que ajudam na comparação. Se você está procurando tênis, comece pela categoria <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:underline">Shoes</a>. Para roupas, explore <a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:underline">Hoodies/Sweaters</a> ou <a href="https://kaspreadsheet.com/search-913/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:underline">T-Shirts</a>.
              </p>
            </div>

            {/* Research */}
            <div id="research" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">{t('beginnersSection3H2')}</h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                {t('beginnersSection3Text')}
              </p>
              <div className="bg-background-100 rounded-xl p-5 mb-4">
                <h3 className="font-semibold text-foreground-950 mb-3">Passos para pesquisar:</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm text-foreground-700">
                  <li>Escolha uma categoria relevante</li>
                  <li>Explore as opções disponíveis</li>
                  <li>Compare diferentes produtos</li>
                  <li>Verifique informações de QC</li>
                  <li>Consulte guias de <Link to="/cssbuy-shipping" className="text-primary-600 hover:underline">shipping</Link></li>
                  <li>Verifique informações atualizadas antes de comprar</li>
                </ol>
              </div>
              <p className="text-foreground-700 leading-relaxed">
                A pesquisa organizada é a chave para encontrar os melhores produtos. Use o <Link to="/cssbuy-spreadsheet" className="text-primary-600 hover:underline">CSSBuy Spreadsheet</Link> como ponto de partida e complemente com sua própria pesquisa adicional.
              </p>
            </div>

            {/* Tips */}
            <div id="tips" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">{t('beginnersSection4H2')}</h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                {t('beginnersSection4Text')}
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-background-100 rounded-xl p-5">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                    <i className="ri-camera-line text-primary-600" style={{ width: 20, height: 20 }} />
                  </div>
                  <h3 className="font-semibold text-foreground-950 mb-2 text-sm">Sempre verifique QC</h3>
                  <p className="text-sm text-foreground-600">Fotos de QC são essenciais para verificar a qualidade antes de aprovar o envio.</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                    <i className="ri-exchange-line text-primary-600" style={{ width: 20, height: 20 }} />
                  </div>
                  <h3 className="font-semibold text-foreground-950 mb-2 text-sm">Compare preços</h3>
                  <p className="text-sm text-foreground-600">Compare preços e métodos de envio antes de decidir.</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                    <i className="ri-file-list-line text-primary-600" style={{ width: 20, height: 20 }} />
                  </div>
                  <h3 className="font-semibold text-foreground-950 mb-2 text-sm">Mantenha registros</h3>
                  <p className="text-sm text-foreground-600">Guarde registros de todas as transações e comunicações.</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                    <i className="ri-shield-check-line text-primary-600" style={{ width: 20, height: 20 }} />
                  </div>
                  <h3 className="font-semibold text-foreground-950 mb-2 text-sm">Use o Safety Center</h3>
                  <p className="text-sm text-foreground-600">Consulte recomendações de segurança antes de comprar.</p>
                </div>
              </div>
            </div>

            {/* Comparison Table */}
            <div id="comparison" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">{t('beginnersSection5H2')}</h2>
              <p className="text-foreground-700 leading-relaxed mb-6">
                {t('beginnersSection5Text')}
              </p>
              <div className="overflow-x-auto rounded-xl border border-background-200/70 mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-100">
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">{t('tableFeature')}</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">{t('tableCssbuy')}</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">{t('tableSuperbuy')}</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800">{t('tableSugargoo')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-background-200/70">
                      <td className="px-4 py-3 text-foreground-700">{t('comparisonProductResearch')}</td>
                      <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                      <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                      <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                    </tr>
                    <tr className="border-t border-background-200/70 bg-background-50">
                      <td className="px-4 py-3 text-foreground-700">{t('comparisonQC')}</td>
                      <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                      <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                      <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                    </tr>
                    <tr className="border-t border-background-200/70">
                      <td className="px-4 py-3 text-foreground-700">{t('comparisonWarehouse')}</td>
                      <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                      <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                      <td className="px-4 py-3"><i className="ri-check-line text-accent-600" style={{ width: 16, height: 16 }} /></td>
                    </tr>
                    <tr className="border-t border-background-200/70 bg-background-50">
                      <td className="px-4 py-3 text-foreground-700">{t('comparisonShipping')}</td>
                      <td className="px-4 py-3 text-foreground-600">{t('tableCheck')}</td>
                      <td className="px-4 py-3 text-foreground-600">{t('tableCheck')}</td>
                      <td className="px-4 py-3 text-foreground-600">{t('tableCheck')}</td>
                    </tr>
                    <tr className="border-t border-background-200/70">
                      <td className="px-4 py-3 text-foreground-700">{t('comparisonBeginnerFriendly')}</td>
                      <td className="px-4 py-3 text-foreground-600">{t('tableCompare')}</td>
                      <td className="px-4 py-3 text-foreground-600">{t('tableCompare')}</td>
                      <td className="px-4 py-3 text-foreground-600">{t('tableCompare')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-foreground-500">{t('comparisonNote')}</p>
            </div>

            {/* Safety */}
            <div id="safety" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">{t('articleSafety')}</h2>
              <p className="text-foreground-700 leading-relaxed mb-4">
                A segurança deve ser sua prioridade ao pesquisar e comprar produtos online. Sempre verifique URLs antes de fazer login, proteja suas senhas com autenticação de dois fatores, e nunca compartilhe informações confidenciais.
              </p>
              <p className="text-foreground-700 leading-relaxed">
                Consulte nosso <Link to="/cssbuy-safety" className="text-primary-600 hover:underline">Safety Center</Link> para recomendações completas sobre como proteger suas transações e informações pessoais durante o processo de compra.
              </p>
            </div>

            {/* FAQ */}
            <div id="faq" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">{t('articleFaq')}</h2>
              <div className="space-y-3">
                <div className="bg-background-100 rounded-xl p-5">
                  <h3 className="font-semibold text-foreground-950 mb-2">Quanto tempo leva para receber os produtos?</h3>
                  <p className="text-sm text-foreground-700 leading-relaxed">O prazo de entrega depende do método de envio escolhido e do destino. Consulte nosso <Link to="/cssbuy-shipping" className="text-primary-600 hover:underline">guia de shipping</Link> para mais detalhes.</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5">
                  <h3 className="font-semibold text-foreground-950 mb-2">Preciso pagar taxas alfandegárias?</h3>
                  <p className="text-sm text-foreground-700 leading-relaxed">Taxas alfandegárias dependem do país de destino e do valor dos produtos. Verifique as regras do seu país antes de enviar.</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5">
                  <h3 className="font-semibold text-foreground-950 mb-2">Como sei se o produto é de boa qualidade?</h3>
                  <p className="text-sm text-foreground-700 leading-relaxed">Sempre verifique as fotos de QC antes de aprovar o envio. Leia nosso <Link to="/cssbuy-qc" className="text-primary-600 hover:underline">guia de QC</Link> para aprender a avaliar a qualidade.</p>
                </div>
              </div>
            </div>

            {/* Related Articles */}
            <div className="mt-10 pt-8 border-t border-background-200/70">
              <h3 className="font-semibold text-foreground-950 mb-4">{t('articleRelated')}</h3>
              <div className="flex flex-wrap gap-3">
                <Link to="/cssbuy-spreadsheet" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">{t('breadcrumbSpreadsheet')}</Link>
                <Link to="/cssbuy-shipping" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">{t('navShipping')}</Link>
                <Link to="/cssbuy-qc" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">QC</Link>
                <Link to="/cssbuy-guide" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">{t('navGuide')}</Link>
                <Link to="/cssbuy-safety" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">{t('navSafety')}</Link>
                <Link to="/cssbuy-review" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">{t('navReview')}</Link>
                <Link to="/" className="px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors">{t('breadcrumbHome')}</Link>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-10 text-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors shadow-lg shadow-primary-500/25 whitespace-nowrap"
              >
                {t('finalCtaButton')}
                <i className="ri-arrow-right-line" style={{ width: 18, height: 18 }} />
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}